import React from "react";
import "./style.scss";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, copied: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  getErrorDetails = () => {
    const { error, errorInfo } = this.state;
    const timestamp = new Date().toISOString();
    const userAgent = navigator.userAgent;
    const url = window.location.href;

    return `=== Error Report ===
Timestamp: ${timestamp}
URL: ${url}
Browser: ${userAgent}

=== Error Message ===
${error?.toString() || "Unknown error"}

=== Stack Trace ===
${error?.stack || "No stack trace available"}

=== Component Stack ===
${errorInfo?.componentStack || "No component stack available"}`;
  };

  handleCopyError = async () => {
    const errorDetails = this.getErrorDetails();
    try {
      await navigator.clipboard.writeText(errorDetails);
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = errorDetails;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    }
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, copied } = this.state;

      return (
        <div className="error-boundary">
          <div className="error-boundary-content">
            <div className="error-boundary-title">Oops! Something went wrong</div>
            <p className="error-boundary-message">
              We are sorry, but something unexpected happened. Please copy the error
              details below and send them to the development team.
            </p>
            <div className="error-boundary-actions">
              <button
                className="error-boundary-btn error-boundary-btn-primary"
                onClick={this.handleReload}
              >
                Refresh Page
              </button>
              <button
                className="error-boundary-btn error-boundary-btn-secondary"
                onClick={this.handleGoHome}
              >
                Go to Home
              </button>
            </div>

            {/* Error Details Section - Always visible */}
            <div className="error-boundary-details-section">
              <div className="error-boundary-details-header">
                <span>Error Details</span>
                <button
                  className="error-boundary-copy-btn"
                  onClick={this.handleCopyError}
                >
                  {copied ? (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M20 6L9 17l-5-5"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Copied!
                    </>
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <rect
                          x="9"
                          y="9"
                          width="13"
                          height="13"
                          rx="2"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                        <path
                          d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                      </svg>
                      Copy Error Details
                    </>
                  )}
                </button>
              </div>
              <div className="error-boundary-error-box">
                <div className="error-boundary-error-label">Error:</div>
                <div className="error-boundary-error-message">
                  {error?.toString() || "Unknown error"}
                </div>
                {error?.stack && (
                  <>
                    <div className="error-boundary-error-label">Stack Trace:</div>
                    <pre className="error-boundary-stack">{error.stack}</pre>
                  </>
                )}
                {errorInfo?.componentStack && (
                  <>
                    <div className="error-boundary-error-label">Component Stack:</div>
                    <pre className="error-boundary-stack">
                      {errorInfo.componentStack}
                    </pre>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
